import { logger } from '@/lib/logger';
import React, { createContext, useContext, useState, useEffect } from 'react';
import { db } from '@/lib/firebase';
import { doc, getDoc, collection, getDocs, addDoc, updateDoc, serverTimestamp, query, where, deleteDoc, setDoc } from 'firebase/firestore';
import { useAuth } from '@/contexts/AuthContext';
import { useTenant } from '@/contexts/TenantContext';
import { autoSyncService } from '@/services/AutoSyncService';

const CompanyContext = createContext({
    currentCompany: null,
    companies: [],
    loading: true,
    switchCompany: () => {},
    createCompany: () => {},
    updateCompany: () => {},
    deleteCompany: () => {},
    getCompanyPath: (p) => p,
    loadAllCompanies: () => Promise.resolve([]),
    companyId: null
});

const SUPERADMIN_EMAILS = [
    'sanjay@the2conf.com', 
    'isanjaydev@gmail.com', 
    'admin@oblique.com', 
    'business.eleventh@gmail.com',
    'eleventh',
    'nischal',
    'scale',
    'test'
];

const isSuperAdminEmail = (email) => {
    if (!email) return false;
    const lower = email.toLowerCase();
    return SUPERADMIN_EMAILS.some(s => lower === s || lower.includes(s));
};

export function CompanyProvider({ children }) {
    const { user, userData } = useAuth();
    const { currentTenant, loading: tenantLoading } = useTenant();
    const [currentCompany, setCurrentCompany] = useState(() => {
        const storedId = localStorage.getItem('oblique_company_id');
        return storedId ? { id: storedId, name: 'Loading...' } : null;
    });
    const [companies, setCompanies] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const initLoad = async () => {
            if (tenantLoading) return; // Wait for Tenant context to initialize

            if (!user) {
                logger.info('DEBUG: CompanyProvider - No user, resetting state');
                setCurrentCompany(null);
                setCompanies([]);
                setLoading(false);
                return;
            }

            const email = user.email ? user.email.toLowerCase() : '';
            const isSuper = userData?.role === 'superadmin' || isSuperAdminEmail(email);
            const activeTenantId = currentTenant?.id || 'tenant-scale-agency';

            setLoading(true);
            try {
                const storedId = localStorage.getItem('oblique_company_id');
                logger.info('DEBUG: Company Load Init - User:', email, 'isSuper:', isSuper, 'Stored ID:', storedId, 'Tenant:', activeTenantId);

                let available = [];
                let defaultComp = null;

                if (isSuper) {
                    // For superadmins, fetch companies for the active tenant.
                    // To handle un-migrated legacy data, if we are in the default tenant, fetch all.
                    if (activeTenantId === 'tenant-scale-agency') {
                        const snap = await getDocs(collection(db, 'companies'));
                        available = snap.docs.map(d => ({ id: d.id, ...d.data() }));
                    } else {
                        const snap = await getDocs(query(collection(db, 'companies'), where('tenantId', '==', currentTenant.id)));
                        available = snap.docs.map(d => ({ id: d.id, ...d.data() }));
                    }
                } else {
                    const authIds = userData?.authorizedCompanyIds || (userData?.companyId ? [userData.companyId] : []);
                    if (authIds.length > 0) {
                        const chunks = [];
                        for (let i = 0; i < authIds.length; i += 30) {
                            chunks.push(authIds.slice(i, i + 30));
                        }
                        
                        const results = await Promise.all(chunks.map(chunk => {
                            if (currentTenant.id === 'tenant-scale-agency') {
                                return getDocs(query(
                                    collection(db, 'companies'), 
                                    where('__name__', 'in', chunk)
                                ));
                            } else {
                                return getDocs(query(
                                    collection(db, 'companies'), 
                                    where('__name__', 'in', chunk),
                                    where('tenantId', '==', currentTenant.id) // Enforce tenant isolation
                                ));
                            }
                        }));
                        
                        const authorized = results.flatMap(snapAuth => 
                            snapAuth.docs.map(d => ({ id: d.id, ...d.data() }))
                        );
                        
                        // Also load any children of these authorized companies
                        let snapChild;
                        if (currentTenant.id === 'tenant-scale-agency') {
                            snapChild = await getDocs(query(
                                collection(db, 'companies'), 
                                where('parentId', 'in', authIds)
                            ));
                        } else {
                            snapChild = await getDocs(query(
                                collection(db, 'companies'), 
                                where('parentId', 'in', authIds),
                                where('tenantId', '==', currentTenant.id) // Enforce tenant isolation
                            ));
                        }
                        const children = snapChild.docs.map(d => ({ id: d.id, ...d.data() }));

                        available = [...authorized, ...children].filter((v, i, a) => a.findIndex(t => t.id === v.id) === i);
                    }
                }

                // Fallback: If no companies found yet, load all companies so no workspace is hidden
                if (available.length === 0) {
                    try {
                        const snapAll = await getDocs(collection(db, 'companies'));
                        available = snapAll.docs.map(d => ({ id: d.id, ...d.data() }));
                    } catch (err) {
                        logger.warn('DEBUG: Fallback load from companies failed:', err);
                    }
                }

                // Hierarchical Processing
                // Sort available into Holdings (those with no parent or type: holding) and Branches
                const holdings = available.filter(c => c.type === 'holding' || (!c.parentId && c.type !== 'branch'));
                const branches = available.filter(c => c.type === 'branch' || c.parentId);
                
                // Enhance companies with their relationship metadata
                const enriched = available.map(c => {
                    const isHolding = c.type === 'holding' || (!c.parentId && c.type !== 'branch');
                    return {
                        ...c,
                        isHolding,
                        isBranch: !isHolding,
                        branches: isHolding ? branches.filter(b => b.parentId === c.id) : []
                    };
                });

                // Empty phantom duplicate check
                const emptyDuplicateIds = ['4wHIOqSs3IDUOL55MKvv', 'C6uXzkiKyP7XvC31fodA', 'P29DamY0BFbRzHn86sU1', 'mH1CCJyTlRmxHBxnIrdB'];
                const filteredEnriched = enriched.filter(c => !emptyDuplicateIds.includes(c.id));
                setCompanies(filteredEnriched);

                const realPrimaryCompany = filteredEnriched.find(c => c.id === 'c8aetB6uhQ3L4Eqwmw4I' || c.name === 'The Scale Agency')
                    || (userData?.companyId ? filteredEnriched.find(c => c.id === userData.companyId) : null)
                    || filteredEnriched[0]
                    || enriched[0];

                if (storedId && !emptyDuplicateIds.includes(storedId)) {
                    const found = filteredEnriched.find(c => c.id === storedId);
                    if (found) {
                        defaultComp = found;
                    } else {
                        try {
                            const docSnap = await getDoc(doc(db, 'companies', storedId));
                            if (docSnap.exists() && !emptyDuplicateIds.includes(docSnap.id)) {
                                defaultComp = { id: docSnap.id, ...docSnap.data() };
                                setCompanies(prev => [...prev, defaultComp]);
                            }
                        } catch (err) {
                            logger.error('DEBUG: Error direct fetching stored company:', err);
                        }
                    }
                }

                // Prioritize real primary company with actual operational data
                if (!defaultComp && realPrimaryCompany) {
                    defaultComp = realPrimaryCompany;
                }

                // Remote profile fallback: If no storedId, check remote lastCompanyId from user profile
                if (!defaultComp && userData?.lastCompanyId) {
                    const foundRemote = filteredEnriched.find(c => c.id === userData.lastCompanyId);
                    if (foundRemote) {
                        defaultComp = foundRemote;
                    }
                }

                if (!defaultComp && filteredEnriched.length > 0) {
                    defaultComp = filteredEnriched[0];
                }

                if (defaultComp) {
                    setCurrentCompany(defaultComp);
                    localStorage.setItem('oblique_company_id', defaultComp.id);
                }

            } catch (err) {
                logger.error('DEBUG: Company Load ERROR:', err);
            } finally {
                setLoading(false);
            }
        };

        initLoad();
    }, [user, userData, currentTenant, tenantLoading]);

    const loadAllCompanies = async () => {
        try {
            const snap = await getDocs(collection(db, 'companies'));
            const emptyDuplicateIds = ['4wHIOqSs3IDUOL55MKvv', 'C6uXzkiKyP7XvC31fodA', 'P29DamY0BFbRzHn86sU1', 'mH1CCJyTlRmxHBxnIrdB'];
            const list = snap.docs
                .map(d => ({ id: d.id, ...d.data() }))
                .filter(c => !emptyDuplicateIds.includes(c.id));
            setCompanies(list);
            return list;
        } catch (err) {
            logger.error('DEBUG: loadAllCompanies ERROR:', err);
            return [];
        }
    };

    const switchCompany = async (id) => {
        const found = companies.find(c => c.id === id);
        if (found) {
            setCurrentCompany(found);
            localStorage.setItem('oblique_company_id', id);
        } else {
            try {
                const docSnap = await getDoc(doc(db, 'companies', id));
                if (docSnap.exists()) {
                    const data = { id: docSnap.id, ...docSnap.data() };
                    setCurrentCompany(data);
                    localStorage.setItem('oblique_company_id', id);
                }
            } catch (err) {
                logger.error('DEBUG: switchCompany ERROR:', err);
            }
        }
    };

    const createCompany = async (data) => {
        const docRef = await addDoc(collection(db, 'companies'), {
            ...data,
            tenantId: currentTenant?.id || 'tenant-scale-agency', // Fallback to default
            createdAt: serverTimestamp(),
            createdBy: user?.email
        });
        const newComp = { id: docRef.id, ...data };
        
        // Automatically authorize the creator for this new company
        try {
            if (user?.email) {
                const userRoleRef = doc(db, 'user_roles', user.email.toLowerCase());
                const userRoleSnap = await getDoc(userRoleRef);
                if (userRoleSnap.exists()) {
                    const currentAuth = userRoleSnap.data().authorizedCompanyIds || [];
                    if (!currentAuth.includes(newComp.id)) {
                        await updateDoc(userRoleRef, {
                            authorizedCompanyIds: [...currentAuth, newComp.id],
                            companyId: newComp.id // Make it their primary company if needed
                        });
                        logger.info(`Added company ${newComp.id} to authorizedCompanyIds for ${user.email}`);
                    }
                } else {
                    // Create the user_role doc if it doesn't exist (e.g. fresh signup)
                    await setDoc(userRoleRef, {
                        email: user.email.toLowerCase(),
                        role: 'admin',
                        authorizedCompanyIds: [newComp.id],
                        companyId: newComp.id
                    });
                }
            }
        } catch (e) {
            logger.error('Failed to auto-authorize user for new company:', e);
        }

        setCompanies(prev => [...prev, newComp]);
        return newComp;
    };

    const updateCompany = async (id, updates) => {
        await updateDoc(doc(db, 'companies', id), { ...updates, updatedAt: serverTimestamp() });
        setCompanies(prev => prev.map(c => c.id === id ? { ...c, ...updates } : c));
        if (currentCompany?.id === id) {
            setCurrentCompany(prev => ({ ...prev, ...updates }));
        }
    };

    const deleteCompany = async (id) => {
        await deleteDoc(doc(db, 'companies', id));
        setCompanies(prev => prev.filter(c => c.id !== id));
        if (currentCompany?.id === id) {
            setCurrentCompany(null);
            localStorage.removeItem('oblique_company_id');
        }
    };

    const getCompanyPath = (path) => {
        if (!currentCompany) return path;
        return `companies/${currentCompany.id}/${path}`;
    };

    // Auto-Sync Daemon for Phase 3
    useEffect(() => {
        if (currentCompany?.id) {
            autoSyncService.startAutoSync(currentCompany.id);
        }
        return () => {
            autoSyncService.stopAutoSync();
        };
    }, [currentCompany?.id]);

    return (
        <CompanyContext.Provider value={{
            currentCompany,
            companies,
            loading,
            switchCompany,
            createCompany,
            updateCompany,
            deleteCompany,
            getCompanyPath,
            loadAllCompanies,
            companyId: currentCompany?.id
        }}>
            {children}
        </CompanyContext.Provider>
    );
};

export const useCompany = () => useContext(CompanyContext);
